import { useBoundaries } from "../../../context/boundary";
import { MapClick } from "../../reactOL/interactions";
import { useCallback } from "react";
import type { MapBrowserEvent } from "ol";
import type { FeatureLike } from "ol/Feature";
import { useNavigate } from "react-router-dom";
import { useMap } from "../../../context/map";
import { getCenter } from "ol/extent";
import { Geometry, MultiPolygon, Polygon } from "ol/geom";

const BoundaryClick = () => {
  const { boundaries, setSelectedBoundary, setOverlayCoordinate } =
    useBoundaries();

  const { map } = useMap();

  const navigate = useNavigate();

  const closeOverlay = useCallback(() => {
    setSelectedBoundary(undefined);
    setOverlayCoordinate(undefined);
    navigate(`/`, { state: { autoFocus: false } });
  }, [setSelectedBoundary, setOverlayCoordinate, navigate]);

  const boundaryClick = useCallback(
    (features: FeatureLike[], e: MapBrowserEvent<any>) => {
      if (features.length > 0) {
        const props =
          getSmallestPolygonFeatureByExtent(features)?.getProperties();
        if (!props) return;
        const id = props["Id"];
        const boundary = boundaries.find((b) => b.id === id) ?? undefined;
        if (!boundary) return;
        const center = getCenter(boundary.envelope);
        const view = map?.getView();
        if (view) {
          const zoom = view.getZoom() ?? 5;
          const resolution = view.getResolutionForZoom(zoom);

          const offsetPixels = 80;
          const offsetMapUnits = resolution * offsetPixels;

          const paddedCenter = [center[0], center[1] + offsetMapUnits];

          view.animate({
            center: paddedCenter,
            zoom: Math.max(zoom, 6),
            duration: 750,
          });
        }
        setOverlayCoordinate(center);
        setSelectedBoundary(boundary);
        navigate(`/boundary/${boundary.urlTag}`, {
          state: { autoFocus: false },
        });
      } else {
        closeOverlay();
      }
    },
    [
      boundaries,
      closeOverlay,
      map,
      setOverlayCoordinate,
      setSelectedBoundary,
      navigate,
    ],
  );

  function getSmallestPolygonFeatureByExtent(
    features: FeatureLike[],
  ): FeatureLike | null {
    let best: FeatureLike | null = null;
    let bestExtentArea = Infinity;

    for (const f of features) {
      const geom = (f as any).getGeometry?.() as Geometry | undefined;
      if (!geom) continue;

      const type = geom.getType();
      if (type !== "Polygon" && type !== "MultiPolygon") continue;

      const extent = geom.getExtent();
      const width = extent[2] - extent[0];
      const height = extent[3] - extent[1];
      const extentArea = width * height;

      if (extentArea < bestExtentArea) {
        bestExtentArea = extentArea;
        best = f;
      }
    }

    return best;
  }

  return <MapClick onClick={boundaryClick} />;
};

export default BoundaryClick;
